'use client';

import { signIn } from '@lib/auth';
import { useMicrocopy } from '@lib/cms/microcopy/client';
import { useFlags } from '@lib/flags/amplitude/client';
import { FEATURE_FLAG_ON, Flags } from '@lib/flags/flagConstants';
import { failingFetcher } from '@lib/http/fetch';
import {
  AddProductListItemV2Request,
  GetProductListItemsV2Response,
  GetProductListsV2Response,
  ProductListItemV2,
  ProductListV2,
} from '@lib/types/api/ProductListV2';
import { debounce } from '@lib/util/debounce';
import { useSession } from 'next-auth/react';
import {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';
import { SnackBar } from 'ui-components';

const DEFAULT_NAMESPACE = 'assetstore';
const DEFAULT_SOURCE_SYSTEM = 'genesis';

const EMPTY_ITEMS_RESPONSE: GetProductListItemsV2Response = {
  items: [],
  total: 0,
  start: 0,
  count: 0,
};

const addItemFetcher = async (
  url: string,
  { arg }: { arg: AddProductListItemV2Request }
): Promise<ProductListItemV2> => {
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(arg),
  });

  if (!response.ok) {
    throw new Error('Failed to add to list');
  }

  return response.json();
};

const removeItemFetcher = async (
  url: string,
  { arg }: { arg: { namespace: string; productId: string } }
): Promise<void> => {
  const response = await fetch(`${url}/${arg.namespace}/${arg.productId}`, {
    method: 'DELETE',
  });

  if (!response.ok && response.status !== 204) {
    throw new Error('Failed to remove from list');
  }
};

type ProductListsContextValue = {
  lists: ProductListV2[];
  defaultList: ProductListV2 | null;
  isLoading: boolean;
  isProductInList: (productId: string) => boolean;
  toggleProductList: (productId: string) => void;
  onNewList: () => void;
  onChangeList: (productId: string) => void;
};

const ProductListsContext = createContext<ProductListsContextValue | null>(
  null
);

const DEFAULT_CONTEXT: ProductListsContextValue = {
  lists: [],
  defaultList: null,
  isLoading: false,
  isProductInList: () => false,
  toggleProductList: () => {},
  onNewList: () => {},
  onChangeList: () => {},
};

export const ProductListsProvider = ({ children }: PropsWithChildren) => {
  const { flags } = useFlags();
  const isProductListsEnabled =
    flags?.[Flags.PRODUCT_LISTS]?.value === FEATURE_FLAG_ON;
  const { status, data: session } = useSession();
  const userId = session?.accessToken?.genesis_user;
  const isAuthenticated = status === 'authenticated';
  const shouldFetch = isAuthenticated && isProductListsEnabled;

  const [snackbar, setSnackbar] = useState<{
    type: 'added' | 'removed';
    listName: string;
    listSlug: string;
    key: number;
  } | null>(null);

  const mc = useMicrocopy('product-lists', {
    'product-lists.added-to': 'Added to',
    'product-lists.removed-from': 'Removed from',
    'product-lists.view-list': 'View list',
    'product-lists.change-list': 'Change list',
    'product-lists.add-to-new-list': 'Add to new list',
    'product-lists.close': 'Close',
  });

  // Fetch user's lists
  const { data: listsData, mutate: mutateLists } =
    useSWR<GetProductListsV2Response>(
      shouldFetch ? '/api/lists' : null,
      failingFetcher
    );

  const lists = useMemo(() => listsData ?? [], [listsData]);

  const defaultList = useMemo(
    () => lists.find((list) => list.isFavorites) ?? lists[0] ?? null,
    [lists]
  );

  // Fetch items in default list
  const itemsCacheKey =
    shouldFetch && defaultList ? `/api/lists/${defaultList.id}/items` : null;

  const { data: itemsData, mutate: mutateItems } =
    useSWR<GetProductListItemsV2Response>(itemsCacheKey, failingFetcher);

  const { trigger: triggerAddItem } = useSWRMutation(
    itemsCacheKey,
    addItemFetcher
  );

  const { trigger: triggerRemoveItem } = useSWRMutation(
    itemsCacheKey,
    removeItemFetcher
  );

  const isProductInList = useCallback(
    (productId: string): boolean => {
      return (
        itemsData?.items?.some((item) => item.productId === productId) ?? false
      );
    },
    [itemsData]
  );

  const addToList = useCallback(
    async (productId: string, list: ProductListV2) => {
      await mutateItems(
        async (currentData) => {
          const newItem = await triggerAddItem({
            namespace: DEFAULT_NAMESPACE,
            productId,
            sourceSystem: DEFAULT_SOURCE_SYSTEM,
          });
          if (!newItem) return currentData ?? EMPTY_ITEMS_RESPONSE;
          const data = currentData ?? EMPTY_ITEMS_RESPONSE;
          return {
            ...data,
            items: [...(data.items ?? []), newItem],
            total: data.total + 1,
          };
        },
        {
          optimisticData: (currentData) => {
            const data = currentData ?? EMPTY_ITEMS_RESPONSE;
            const tempItem: ProductListItemV2 = {
              id: `temp-${Date.now()}`,
              namespace: DEFAULT_NAMESPACE,
              productId,
              sourceSystem: DEFAULT_SOURCE_SYSTEM,
              addedAt: new Date().toISOString(),
            };
            return {
              ...data,
              items: [...(data.items ?? []), tempItem],
              total: data.total + 1,
            };
          },
          rollbackOnError: true,
          revalidate: false,
        }
      );

      setSnackbar({
        type: 'added',
        listName: list.name,
        listSlug: list.slug,
        key: Date.now(),
      });
      await mutateLists();
    },
    [mutateItems, triggerAddItem, mutateLists]
  );

  const removeFromList = useCallback(
    async (productId: string, list: ProductListV2) => {
      await mutateItems(
        async (currentData) => {
          await triggerRemoveItem({
            namespace: DEFAULT_NAMESPACE,
            productId,
          });
          if (!currentData) return EMPTY_ITEMS_RESPONSE;
          return {
            ...currentData,
            items: (currentData.items ?? []).filter(
              (item) => item.productId !== productId
            ),
            total: currentData.total - 1,
          };
        },
        {
          optimisticData: (currentData) => {
            if (!currentData) return EMPTY_ITEMS_RESPONSE;
            return {
              ...currentData,
              items: (currentData.items ?? []).filter(
                (item) => item.productId !== productId
              ),
              total: currentData.total - 1,
            };
          },
          rollbackOnError: true,
          revalidate: false,
        }
      );

      setSnackbar({
        type: 'removed',
        listName: list.name,
        listSlug: list.slug,
        key: Date.now(),
      });
      await mutateLists();
    },
    [mutateItems, triggerRemoveItem, mutateLists]
  );

  const toggleRef = useRef({
    isAuthenticated,
    defaultList,
    isProductInList,
    addToList,
    removeFromList,
  });
  toggleRef.current = {
    isAuthenticated,
    defaultList,
    isProductInList,
    addToList,
    removeFromList,
  };

  const toggleProductList = useMemo(
    () =>
      debounce(async (productId: string) => {
        const {
          isAuthenticated,
          defaultList,
          isProductInList,
          addToList,
          removeFromList,
        } = toggleRef.current;

        if (!isAuthenticated) {
          signIn();
          return;
        }

        if (!defaultList) {
          console.error('No default list available');
          return;
        }

        try {
          if (isProductInList(productId)) {
            await removeFromList(productId, defaultList);
          } else {
            await addToList(productId, defaultList);
          }
        } catch (error) {
          console.error('Error toggling product in list:', error);
        }
      }, 300),
    []
  );

  // Placeholder actions for future implementation
  const onNewList = useCallback(() => {
    // TODO: Open "Create new list" modal
    console.log('onNewList - placeholder');
  }, []);

  const onChangeList = useCallback((productId: string) => {
    // TODO: Open "Change list" modal for the product
    console.log('onChangeList - placeholder', productId);
  }, []);

  const handleSnackbarClose = useCallback(
    () =>
      setSnackbar((current) =>
        current?.key === snackbar?.key ? null : current
      ),
    [snackbar?.key]
  );

  const snackbarProps = useMemo(() => {
    if (!snackbar) return null;

    const hasMultipleLists = lists.length > 1;

    if (snackbar.type === 'removed') {
      return {
        iconWeight: 'bold' as const,
        closeIcon: 'X',
        microcopy: {
          title: mc['product-lists.removed-from'],
          actionLabel: snackbar.listName,
          closeAriaLabel: mc['product-lists.close'],
        },
      };
    }

    return {
      iconWeight: 'fill' as const,
      actionIcon: 'ArrowSquareOut',
      closeIcon: hasMultipleLists ? 'CaretDown' : 'Plus',
      microcopy: {
        title: mc['product-lists.added-to'],
        actionAriaLabel: mc['product-lists.view-list'],
        actionLabel: snackbar.listName,
        closeAriaLabel: hasMultipleLists
          ? mc['product-lists.change-list']
          : mc['product-lists.add-to-new-list'],
      },
      onActionClick: userId
        ? () => window.open(`/users/${userId}/${snackbar.listSlug}`, '_blank')
        : undefined,
      onCloseIconClick: hasMultipleLists ? () => onChangeList('') : onNewList,
    };
  }, [snackbar, lists.length, userId, onNewList, onChangeList]);

  const value = useMemo(
    () => ({
      lists,
      defaultList,
      isLoading: !listsData && isAuthenticated,
      isProductInList,
      toggleProductList,
      onNewList,
      onChangeList,
    }),
    [
      lists,
      defaultList,
      listsData,
      isAuthenticated,
      isProductInList,
      toggleProductList,
      onNewList,
      onChangeList,
    ]
  );

  return (
    <ProductListsContext.Provider value={value}>
      {children}
      {snackbarProps && snackbar && (
        <SnackBar
          key={snackbar.key}
          icon="BookmarkSimple"
          onClose={handleSnackbarClose}
          {...snackbarProps}
        />
      )}
    </ProductListsContext.Provider>
  );
};

export const useProductLists = () => {
  const context = useContext(ProductListsContext);
  return context ?? DEFAULT_CONTEXT;
};
